import {
  FormSubmitButton,
  FormSwitchField,
  FormProvider,
  useForm,
} from '@mfw/forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntl, Button } from '@mfw/components';
import Box from '@mui/material/Box';

import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';
import type { UiUserGoalsStep } from '../../../../../schemas/users.schema';

import { UiUserGoalsStepSchema } from '../../../../../schemas/users.schema';
import { useUpdateUser } from '../../../../../hooks/usersv2/useUpdateUser';
import usePublicGoals from '../../../../../hooks/usePublicGoals';
import { getLabelFromTranslations } from '../../../../../utils';
import OnboardingHeader from '../../../OnboardingHeader';
import { useAuthContext } from '../../../../../context';
import { useWizard } from '../../../../../allFeatures';
import generalMessages from '../../../../../messages';
import messages from '../FitnessGoalsStep/messages';

export const FitnessGoalsDialogStep = () => {
  const { currentUser } = useAuthContext();
  const { formatMessage, locale } = useIntl();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const { mutateAsync: updateUserV2Async } = useUpdateUser();

  const methods = useForm({
    defaultValues: {
      fitnessGoals: currentUser?.fitnessProfile?.fitnessGoals ?? [],
    },
    resolver: zodResolver(UiUserGoalsStepSchema),
  });

  const handleNext = async (data: UiUserGoalsStep) => {
    await updateUserV2Async({
      fitnessProfile: {
        fitnessGoals: data.fitnessGoals,
      },
    });
    goToStep('healthIssues');
  };

  const { data, fetchNextPage } = usePublicGoals({
    filters: { limit: '50', orderBy: 'name', status: 'active' },
  });

  const value = methods.watch('fitnessGoals') as {
    checked: boolean;
    value: string;
  }[];

  const isDisabled = function (fitnessGoalId: string) {
    const filteredValue = value?.filter(Boolean);
    const isAlreadyChecked = filteredValue.some((value) => {
      return value.checked && value.value === fitnessGoalId;
    });
    if (isAlreadyChecked) return false;
    return filteredValue.filter((value) => value.checked).length >= 3;
  };

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={<p>{formatMessage(messages.description)}</p>}
          title={formatMessage(messages.title)}
        />
      </Box>
      <Box px={3}>
        {data?.data?.map((goal, index) => {
          return (
            <FormSwitchField
              defaultChecked={currentUser?.fitnessProfile?.fitnessGoals?.some(
                (fitnessGoal) =>
                  fitnessGoal.value === goal.id && fitnessGoal.checked,
              )}
              label={getLabelFromTranslations(locale, goal.translations)}
              isObserved={index === data.data.length - 1}
              data-testid="client-onboarding-goal"
              name={`fitnessGoals.${index}`}
              onIntersection={fetchNextPage}
              disabled={isDisabled(goal.id)}
              value={goal.id}
              key={goal.id}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          onClick={() => goToStep('trainingLevel')}
          data-testid="onboarding-back"
          variant="outlined"
          size="large"
        >
          {formatMessage(generalMessages.back)}
        </Button>
        <FormSubmitButton
          data-testid="onboarding-next"
          variant="contained"
          disableIfNotDirty
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};
